import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Typography,
  Button,
  Box,
  Alert,
} from "@mui/material";
import { Share2, X, Copy, Mail, CheckCircle, Send } from "react-feather";

export default function ShareDialog({
  open,
  onClose,
  link,
  linkCopied,
  onCopyLink,
  onSendEmail,
  theme,
}) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSendEmail = async () => {
    if (!email) {
      setEmailError("Inserisci un indirizzo email");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Inserisci un indirizzo email valido");
      return;
    }

    setEmailError("");
    setIsSending(true);

    try {
      await onSendEmail(email);
      setEmailSent(true);
      setTimeout(() => {
        setEmailSent(false);
        setEmail("");
        onClose();
      }, 2000);
    } catch (error) {
      setEmailError("Errore durante l'invio. Riprova.");
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setEmailError("");
    setEmailSent(false);
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Share2 size={20} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Condividi Analisi Rischio
            </Typography>
          </Stack>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ color: theme.palette.text.secondary }}
          >
            <X size={20} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            Copia il link sicuro per condividere una vista web del report con
            clienti o colleghi.
          </Typography>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block", fontWeight: 600 }}
            >
              Link di condivisione (valido 30 giorni)
            </Typography>
            <TextField
              fullWidth
              value={link}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={onCopyLink}
                      edge="end"
                      sx={{
                        color: linkCopied
                          ? "success.main"
                          : theme.palette.text.secondary,
                      }}
                    >
                      {linkCopied ? (
                        <CheckCircle size={18} />
                      ) : (
                        <Copy size={18} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { fontSize: 14, fontFamily: "monospace", borderRadius: 2 },
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1.5, display: "block", fontWeight: 600 }}
            >
              Oppure invia tramite Email
            </Typography>

            {emailSent && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                Email inviata con successo!
              </Alert>
            )}

            <Stack spacing={1.5}>
              <TextField
                fullWidth
                type="email"
                placeholder="esempio@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                error={!!emailError}
                helperText={emailError}
                size="small"
                disabled={isSending || emailSent}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail size={16} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 },
                }}
              />

              <Button
                variant="contained"
                startIcon={<Send size={16} />}
                onClick={handleSendEmail}
                disabled={isSending || emailSent || !email}
                fullWidth
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  py: 1.2,
                }}
              >
                {isSending ? "Invio in corso..." : "Invia Analisi"}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
